import React, { memo, useState } from 'react'
import { makeStyles } from '@material-ui/styles'
import ButtonBase from '@material-ui/core/ButtonBase'
import classNames from 'classnames'
import CircularProgress from '@material-ui/core/CircularProgress'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/react-hooks'
import { useHistory, useParams } from 'react-router-dom'
import AlertIcon from '@material-ui/icons/NotificationImportantOutlined'

import { Textfield } from '../'
import styles from './styles'
import { AuthStore } from '../../reducer'
import { ForgotPasswordMutation, UpdateForgotPasswordMutation } from 'store'
import {
	TForgotPasswordProps,
	EAuthTextfieldType,
	EAuthActionTypes,
	EAuthSectionTypes,
} from 'models'

const useStyles = makeStyles(styles)

const ForgotPasswordView: React.FunctionComponent<TForgotPasswordProps> = props => {
	const {
		validation,
		success,
		error,
		values: { email, password },
		dispatch,
	} = props
	const history = useHistory()
	const { token }: { token?: string } = useParams()
	const { t, i18n } = useTranslation()
	const [loading, toggle] = useState(false)
	const classes = useStyles()
	const [forgotPasswordMutationHandle] = useMutation(ForgotPasswordMutation)
	const [updateForgotPasswordMutationHandle] = useMutation(
		UpdateForgotPasswordMutation,
	)
	const goToLogin = () => {
		dispatch({
			type: EAuthActionTypes.setSection,
			section: EAuthSectionTypes.login,
		})
	}
	const recoverPassword = async () => {
		toggle(true)
		try {
			const { data }: any = await forgotPasswordMutationHandle({
				variables: {
					email,
					language: i18n.language,
				},
			})
			if (data && data.forgotPassword && data.forgotPassword.status) {
				dispatch({
					type: EAuthActionTypes.setForgotPasswordSuccess,
				})
			} else {
				dispatch({
					type: EAuthActionTypes.setForgotPasswordFailed,
				})
			}
		} catch (error) {
			dispatch({
				type: EAuthActionTypes.setForgotPasswordFailed,
			})
		}
		toggle(false)
	}
	const updatePassword = async () => {
		const { data }: any = await updateForgotPasswordMutationHandle({
			variables: {
				token,
				password,
				language: i18n.language,
			},
		})
		if (data.updateForgotPassword && data.updateForgotPassword.status)
			dispatch({
				type: EAuthActionTypes.setUpdateForgotPasswordSuccess,
			})
		else
			dispatch({
				type: EAuthActionTypes.setUpdateForgotPasswordFailed,
			})
		history.replace(`/${i18n.language}/auth`)
		dispatch({
			type: EAuthActionTypes.setSection,
			section: EAuthSectionTypes.login,
		})
	}

	return (
		<>
			<div className={classes.errorMessagesContainer}>
				{success.forgotPassword && (
					<div className={classes.accountConfirmation}>
						{t(
							'If the address exists in our database, you will receive an email with the password recovery instructions.',
						)}
					</div>
				)}
				{error.forgotPassword && (
					<div
						className={classNames([
							classes.accountConfirmation,
							classes.messageWithIcon,
						])}
					>
						<AlertIcon />
						<span>
							{t(
								'Sorry, this mail could not be sent at this time. Please try again later.',
							)}
						</span>
					</div>
				)}
				{error.updateForgotPassword && (
					<div
						className={classNames([
							classes.accountConfirmation,
							classes.messageWithIcon,
						])}
					>
						<AlertIcon />
						<span>
							{t('Sorry we could not update your password')}
						</span>
					</div>
				)}
			</div>
			<div className={classes.container}>
				{!token && <Textfield type={EAuthTextfieldType.email} />}
				{token && <Textfield type={EAuthTextfieldType.password} />}
				<ButtonBase
					className={classNames(classes.loginButton, {
						[classes.loginButtonActive]:
							(!token && validation.email && !loading) ||
							(token && validation.password && !loading),
					})}
					disabled={
						(!token && !validation.email) ||
						(token && !validation.password) ||
						loading
					}
					onClick={token ? updatePassword : recoverPassword}
				>
					{!loading &&
						t(token ? 'Update Password' : 'Recover Password')}
					{loading && (
						<CircularProgress
							className={classes.loading}
							size={20}
						/>
					)}
				</ButtonBase>
			</div>
			<div className={classes.createAccount}>
				<span>{t('Back to')}</span>
				<ButtonBase onClick={goToLogin}>{t('Log In')}</ButtonBase>
			</div>
		</>
	)
}

export default memo((props: any) => (
	<AuthStore keys={['validation', 'success', 'error', 'values', 'dispatch']}>
		<ForgotPasswordView {...props} />
	</AuthStore>
))
